import * as Sentry from "@sentry/vue";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localeData from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import { StatusCodes } from "http-status-codes";
import { createPinia } from "pinia";
import PrimeVue from "primevue/config";
import { createApp, h } from "vue";
import { GlobalEvents } from "vue-global-events";
import VueTippy from "vue-tippy";
import App from "./App.vue";
import { initializeLogging, log } from "./common/utils/logging";
import headerComponent from "./components/HeaderComponent.vue";
import NavigationComponent from "./components/NavigationComponent.vue";
import { isInstalling, isRefreshNeeded } from "./components/ServiceWorkerUpdatePopupComponent";
import { useUser } from "./composition-functions/user";
import "./css/index.css";
import { CustomValidationError } from "./errors/CustomValidationError";
import { GenericFailure } from "./errors/GenericFailure";
import { getGlobalTranslation, i18n } from "./i18n";
import { AnexisPreset } from "./primeVue";
import router from "./router";
import { setToastMessage } from "./util/SweetalertHelper";
import "./util/arrayHelper";
import "./util/monacoEditor";

dayjs.extend(utc);
dayjs.extend(isBetween);
dayjs.extend(localizedFormat);
dayjs.extend(localeData);
dayjs.extend(isSameOrBefore);
const pinia = createPinia();

const app = createApp({
  render: () => h(App),
});

app.use(pinia);
app.use(VueTippy);
app.use(i18n);
app.use(router);
app.use(PrimeVue, {
  theme: {
    preset: AnexisPreset,
    options: {
      darkModeSelector: ".dark",
    },
  },
});

app.component("GlobalEvents", GlobalEvents);
app.component("HeaderComponent", headerComponent);
app.component("NavigationComponent", NavigationComponent);

initializeLogging({ app, router });

app.config.errorHandler = (error) => {
  if (error instanceof GenericFailure) {
    if (error.statusCode === StatusCodes.UNAUTHORIZED) {
      const userStore = useUser();
      const { resetAuthenticationState } = userStore;
      resetAuthenticationState();
      if (router.currentRoute.value.name && router.currentRoute.value.name !== "login") {
        router.push({ name: "login", query: { redirectPath: router.currentRoute.value.fullPath } });
      }
      setToastMessage({ icon: "error", title: `${error.message}${error.description ? `\n${error.description}` : ""}` });
    } else {
      if (isRefreshNeeded.value || isInstalling.value) {
        showUpdateMessage();
        return;
      }
      Sentry.withScope((scope) => {
        scope.setTag("logSource", "app.config.errorHandler");
        log({ error });
      });

      if (error instanceof CustomValidationError) {
        setToastMessage({ icon: "error", title: error.message });
        document.querySelector("[data-has-errors='true']")?.scrollIntoView({ block: "center", inline: "center" });
      } else {
        setToastMessage({ icon: "error", title: `${error.message}${error.description ? `\n${error.description}` : ""}` });
      }
    }
  } else {
    if (isRefreshNeeded.value || isInstalling.value) {
      showUpdateMessage();
      return;
    }
    Sentry.withScope((scope) => {
      scope.setTag("logSource", "app.config.errorHandler");
      log({ error });
    });

    setToastMessage({ icon: "error", title: getGlobalTranslation({ message: "message.somethingWentWrong" }) });
  }
};

function showUpdateMessage() {
  if (isInstalling.value) {
    setToastMessage({ icon: "warning", title: getGlobalTranslation({ message: "message.installingUpdates" }) });
  }
  if (isRefreshNeeded.value) {
    setToastMessage({ icon: "warning", title: getGlobalTranslation({ message: "message.updatesInstalled" }) });
  }
}

window.addEventListener("unhandledrejection", (error) => {
  Sentry.withScope((scope) => {
    scope.setTag("logSource", "window.onunhandledrejection");
    log({ error });
  });
});

app.mount("#app");
